/* eslint-disable @typescript-eslint/naming-convention */

import { Children, useEffect, useState } from 'react';

import { useDetermineMobile } from '@myrealtrip/device-detect';

import styled from '@emotion/styled';
import { LocalNavVerticalItem } from '../../../constants/lnb';
import { useLayoutHeaderContext } from '../../../modules/LayoutHeaderContext';

import LocalNavCarousel from './LocalNavCarousel';
import LocalNavItem from './LocalNavItem';
import LocalNavSkeleton from './LocalNavSkeleton';
import RestLocalNavItems from './RestLocalNavItems';
import { isActiveMenu } from '../../utils/isActiveMenu';

const MAX_DISPLAY_VERTICAL_LENGTH = 9;

type NormalizedLocalNavVerticalItem = LocalNavVerticalItem & {
  determinedMenuUrl: string;
};

export default function DefaultLocalNavItems() {
  const [initialSlideIndex, setInitialSlideIndex] = useState(0);
  const { lnbVerticalItems, isFlightMode, gnbLoading } = useLayoutHeaderContext();
  const isMobile = useDetermineMobile();
  // PST-570 — 비홈 헤더 nav 에서 '홈' 탭 제거(메인 헤더와 정합). lnbVerticalItems 만 position 순 정렬.
  const mergedVerticalItems = [...lnbVerticalItems].sort((a, b) => a.position - b.position);
  const normalizedVerticalItems: NormalizedLocalNavVerticalItem[] = mergedVerticalItems.map(
    (item) => {
      const { link } = item;
      const determinedMenuUrl = isMobile ? link.mobile_web : link.desktop;

      return { ...item, determinedMenuUrl };
    },
  );
  const displayVerticalItems = normalizedVerticalItems.slice(0, MAX_DISPLAY_VERTICAL_LENGTH);
  const restVerticalItems = normalizedVerticalItems
    .slice(displayVerticalItems.length)
    .map(({ link, title, cms_icon_url }) => ({
      link: link.desktop,
      label: title,
      iconUrl: cms_icon_url,
    }));

  useEffect(() => {
    setInitialSlideIndex(
      normalizedVerticalItems.findIndex(({ code, link }) =>
        isActiveMenu({ code, menuUrl: isMobile ? link.mobile_web : link.desktop, isFlightMode }),
      ),
    );
  }, [normalizedVerticalItems, isMobile, isFlightMode]);

  // PST-570 — GNB self-fetch 중이면 스켈레톤(default→region 스왑 flash 방지).
  if (gnbLoading) {
    return (
      <Container>
        <LocalNavSkeleton />
      </Container>
    );
  }

  return (
    <Container>
      {isMobile && (
        <LocalNavCarousel initialSlideIndex={initialSlideIndex}>
          {Children.toArray(
            normalizedVerticalItems?.map(
              ({ title, code, determinedMenuUrl, lnb_required_badge }) => (
                <LocalNavItem
                  label={title}
                  code={code}
                  link={determinedMenuUrl}
                  showNewBadge={lnb_required_badge}
                />
              ),
            ),
          )}
        </LocalNavCarousel>
      )}
      {!isMobile && (
        <>
          {Children.toArray(
            displayVerticalItems?.map(
              ({ title, code, determinedMenuUrl, lnb_required_badge, cms_icon_url }) => (
                <LocalNavItem
                  label={title}
                  code={code}
                  link={determinedMenuUrl}
                  showNewBadge={lnb_required_badge}
                  showIcon
                  iconUrl={cms_icon_url}
                />
              ),
            ),
          )}
          {restVerticalItems.length > 0 && (
            <RestLocalNavItems verticalItems={restVerticalItems} showIcon />
          )}
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: space-between;
`;
